import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Icon } from 'components/ui';
import { Logo } from 'components/Logo';
import UserAuthAvatar from 'components/UserAuthAvatar';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
  showAvatar?: boolean;
  showLogo?: boolean;
  showBackButton?: boolean;
  backButtonText?: string;
  backButtonPath?: string;
  onBackButtonClick?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  badge,
  badgeVariant = 'default',
  showAvatar = true,
  showLogo = true,
  showBackButton = false,
  backButtonText = 'Back to Dashboard',
  backButtonPath = '/dashboard/assistant',
  onBackButtonClick,
  className = '',
  children
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Navigation Header */}
      {showLogo && (
        <div className="flex max-w-7xl mx-auto mt-8 justify-between items-center px-4">
          <Logo />
          {showAvatar && (
            <UserAuthAvatar />
          )}
        </div>
      )}

      {/* Back Button */}
      {showBackButton && (
        <div className="max-w-7xl mx-auto px-4 mt-6">
          <button
            onClick={() => onBackButtonClick ? onBackButtonClick() : navigate(backButtonPath)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 group transition-colors"
          >
            <Icon type="backArrow" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">{backButtonText}</span>
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="text-center mb-8">
        {badge && (
          <Badge className={`text-xs rounded-lg py-1 px-3 ${badgeVariant === 'default' ? 'bg-gray-100' : ''} ${className}`}>
            {badge}
          </Badge>
        )}
        <h1 className="text-2xl md:text-5xl w-11/12 md:w-3/4 mx-auto mt-4 md:mt-6 font-medium leading-tight text-center">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-text-secondary mb-6">{subtitle}</p>
        )}
        {children}
      </div>
    </>
  );
};

export { PageHeader };